import React from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { get, sortAndMutatePdsData, executeSelector } from 'ctm-util-functions';
import { Text } from '../../text/text';
import { SimpleFlexbox } from '../../flexbox/flexbox';
import { Button } from '../../button/button';

const PRODUCT_DISCLOSURE_STATEMENT = 'Product Disclosure Statement';

/**
 * Render Product Disclosure Statement Text and Buttons
 * @param {String} stateKey StateKey for product Disclosures list/map
 * @param {String} content The paragraph PDS content
 * @param {String} agreement The agreement PDS with links
 * @param {Boolean} showKeyFacts The boolean to show or hide key facts paragraph
 * @param {Object} stateProps - The state from the application
 * @returns {Node} ProductDisclosureStatement component
 */
const ProductDisclosureStatement = ({ stateKey, content, showKeyFacts, agreement, stateProps }) => {
    const productDisclosures = get(stateProps, stateKey);
    if (!productDisclosures) return null;
    const pdsButtons = renderPdsButtons(productDisclosures);
    const keyFacts = showKeyFacts ? renderKeyFacts(productDisclosures, agreement, stateProps) : null;
    const keyFactsComponent = showKeyFacts ? <Text innerHTML={keyFacts} tagName="p" /> : null;

    return (
        <SimpleFlexbox display="flex" margin="bottom" column>
            <p>{content}</p>
            {pdsButtons}
            {keyFactsComponent}
        </SimpleFlexbox>
    );
};

/**
 * Iterates productDisclosures to return updated agreement with KeyFacts anchors
 * @param {Immutable.List} productDisclosures Map of productDisclosures
 * @param {String} agreement productDisclosure agreement
 * @param {Object} stateProps - The state from the application
 * @returns {String} agreement text with anchor links changed
 */
const renderKeyFacts = (productDisclosures, agreement, stateProps) => {
    const keyFactSheets = productDisclosures
        .filter((item, key) => {
            return `${key}`.includes('kfs') && item != null;
        })
        .map((item, key) => ({
            kfs: key,
            link: `<a target='_blank' href='${item}'>`
        }))
        .toArray();

    let updatedAgreement = agreement;

    if (updatedAgreement.indexOf('selector:') > -1) {
        updatedAgreement = executeSelector(agreement, stateProps);
    }

    for (const linkData of keyFactSheets) {
        updatedAgreement = updatedAgreement.replace(`<a data-kfs='${linkData.kfs}'>`, linkData.link);
    }
    return updatedAgreement;
};

/**
 * Iterates over productDisclosures to return PDS button links to render
 * @param {Immutable.Map} productDisclosures Map of productDisclosures
 * @returns {Array} of PDS Buttons
 */
const renderPdsButtonsFromMap = (productDisclosures) => {
    const pdsList = productDisclosures.filter((disclosure, key) => {
        return `${key}`.includes('pds') && disclosure !== null && disclosure.length > 0;
    });
    return pdsList
        .map((item, key) => {
            const labelLetter = `${key}`.replace('pds', '').toUpperCase();
            const label = pdsList.size === 1 ? 'Product Disclosure Statement' : `Product Disclosure ${labelLetter}`;
            return <Button key={labelLetter} label={label} href={item} target="_blank" UIComponent="btn_primary_transparent_small_semi_rounded" variant="margin_bottom" />;
        })
        .toArray();
};

/**
 * Iterates over productDisclosures to return PDS button links to render
 * @param {Immutable.List|Immutable.Map} productDisclosures Map of productDisclosures
 * @returns {Array} of PDS Buttons
 */
const renderPdsButtons = (productDisclosures) => {
    if (Immutable.Map.isMap(productDisclosures)) {
        return renderPdsButtonsFromMap(productDisclosures);
    }

    const sortedPdsList = sortAndMutatePdsData(productDisclosures);
    return sortedPdsList.map((pds) => (
        <Button key={pds.get('key')} href={pds.get('url')} target="_blank" UIComponent="btn_primary_transparent_small_semi_rounded" variant="margin_bottom">
            {sortedPdsList.size > 1 || pds.get('code') === 'PEG' || pds.get('code') === 'PDS' ? pds.get('title') : PRODUCT_DISCLOSURE_STATEMENT}
        </Button>
    ));
};

ProductDisclosureStatement.propTypes = {
    stateKey: PropTypes.string,
    content: PropTypes.string,
    showKeyFacts: PropTypes.bool,
    agreement: PropTypes.string,
    stateProps: PropTypes.object
};

export { ProductDisclosureStatement };
